import { useEffect, useRef, useState } from 'react';
import { photoSrc } from '../api';
import type { Photo } from '../types';

const OUT_W = 1000;
const OUT_H = 1250; // 4:5, matches the gallery and profile cards
const ASPECT = OUT_W / OUT_H;

interface Props {
  photo: Photo;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
  onReplace: (id: string, file: File) => Promise<void>;
}

/**
 * Full-screen view of one gallery photo. Delete, or enter crop mode: the image
 * sits behind a fixed 4:5 frame; drag to pan and use the slider (or pinch) to
 * zoom, then Save renders the framed region to a JPEG and replaces the photo.
 */
export function PhotoLightbox({ photo, onClose, onDelete, onReplace }: Props) {
  const [mode, setMode] = useState<'view' | 'crop'>('view');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // pan, in frame pixels
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number; pinch?: number; zoom0?: number } | null>(null);

  useEffect(() => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => setImg(el);
    el.src = photoSrc(photo.url)!;
  }, [photo.url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Geometry: the image is scaled so it covers the frame at zoom 1, then zoomed and panned.
  const frameW = Math.min(window.innerWidth - 32, 420);
  const frameH = frameW / ASPECT;
  const cover = img ? Math.max(frameW / img.width, frameH / img.height) : 1;
  const scale = cover * zoom;
  const drawW = img ? img.width * scale : 0;
  const drawH = img ? img.height * scale : 0;
  const clamp = (o: { x: number; y: number }, z = zoom) => {
    const s = cover * z;
    const w = (img?.width ?? 0) * s, h = (img?.height ?? 0) * s;
    const maxX = Math.max(0, (w - frameW) / 2), maxY = Math.max(0, (h - frameH) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, o.x)), y: Math.min(maxY, Math.max(-maxY, o.y)) };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setOffset(clamp({ x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) }));
  };
  const onPointerUp = () => { drag.current = null; };
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    if (!drag.current?.pinch) { drag.current = { ...(drag.current ?? { x: 0, y: 0, ox: offset.x, oy: offset.y }), pinch: d, zoom0: zoom }; return; }
    const z = Math.min(4, Math.max(1, (drag.current.zoom0 ?? 1) * (d / drag.current.pinch)));
    setZoom(z);
    setOffset((o) => clamp(o, z));
  };

  const saveCrop = async () => {
    if (!img) return;
    setBusy(true);
    setError(null);
    try {
      // Frame → source rectangle.
      const sx = (drawW / 2 - frameW / 2 - offset.x) / scale;
      const sy = (drawH / 2 - frameH / 2 - offset.y) / scale;
      const sw = frameW / scale, sh = frameH / scale;
      const canvas = document.createElement('canvas');
      canvas.width = OUT_W;
      canvas.height = OUT_H;
      canvas.getContext('2d')!.drawImage(img, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.88));
      if (!blob) throw new Error('Could not render the crop');
      await onReplace(photo.id, new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
      onClose();
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirm('Delete this photo?')) return;
    setBusy(true);
    try { await onDelete(photo.id); onClose(); } catch (e: any) { setError(e.message); setBusy(false); }
  };

  return (
    <div className="lightbox" role="dialog" aria-modal="true">
      <div className="lightbox-top">
        <button className="btn ghost" onClick={onClose}>✕</button>
        <span className="muted small">{mode === 'crop' ? 'Drag to move · pinch or slide to zoom' : ''}</span>
        <span />
      </div>
      <div className="lightbox-body">
        {mode === 'view' ? (
          <img src={photoSrc(photo.url)!} alt="" className="lightbox-img" />
        ) : (
          <div ref={frameRef} className="crop-frame" style={{ width: frameW, height: frameH }}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onTouchMove={onTouchMove}>
            {img && <img src={img.src} alt="" draggable={false} style={{ width: drawW, height: drawH, transform: `translate(${(frameW - drawW) / 2 + offset.x}px, ${(frameH - drawH) / 2 + offset.y}px)` }} />}
          </div>
        )}
      </div>
      {error && <div className="error" style={{ textAlign: 'center' }}>{error}</div>}
      <div className="lightbox-actions">
        {mode === 'view' ? (
          <>
            <button className="btn danger" disabled={busy} onClick={del}>Delete</button>
            <button className="btn" disabled={busy || !img} onClick={() => { setMode('crop'); setZoom(1); setOffset({ x: 0, y: 0 }); }}>Crop</button>
          </>
        ) : (
          <>
            <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => { const z = Number(e.target.value); setZoom(z); setOffset((o) => clamp(o, z)); }} aria-label="Zoom" />
            <button className="btn secondary" disabled={busy} onClick={() => setMode('view')}>Cancel</button>
            <button className="btn" disabled={busy} onClick={saveCrop}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        )}
      </div>
    </div>
  );
}
